import { Sparkles, Coins, Layers, Droplet } from 'lucide-react';
import { useMemo } from 'react';
import { useCollection } from '@/hooks/useCollection';
import { formatPrice } from '@/lib/card-prices';
import { collectionLineValue, collectionUnitPrice } from '@/lib/collectionValue';
import { PriceMoversCard } from '@/components/PriceMoversCard';

export function CollectionInsights() {
  const { data: collection } = useCollection();

  const insights = useMemo(() => {
    const items = collection || [];
    let totalValue = 0;
    let totalCards = 0;
    let inkable = 0;
    let nonInkable = 0;
    let enchantedValue = 0;
    const valuedItems: { name: string; value: number; ink: string }[] = [];

    for (const item of items) {
      const c = item.card;
      if (!c) continue;
      const qty = item.quantity || 1;
      const lineValue = collectionLineValue(item);
      totalValue += lineValue;
      totalCards += qty;
      if (c.inkable) inkable += qty; else nonInkable += qty;
      if (c.enchanted || item.print?.treatment === 'enchanted') enchantedValue += lineValue;
      if (collectionUnitPrice(item) > 0) valuedItems.push({ name: c.name, value: lineValue, ink: c.ink_color });
    }

    valuedItems.sort((a, b) => b.value - a.value);

    return {
      totalValue,
      totalCards,
      uniqueCards: items.length,
      inkable,
      nonInkable,
      enchantedValue,
      topValued: valuedItems.slice(0, 3),
    };
  }, [collection]);

  if (!collection?.length) return null;

  const inkablePct = insights.totalCards > 0 ? Math.round((insights.inkable / insights.totalCards) * 100) : 0;

  return (
    <div className="mt-4 rounded-2xl border border-border bg-gradient-to-br from-card via-card to-primary/5 p-4 animate-fade-in">
      <div className="flex items-center gap-1.5 mb-3">
        <Sparkles className="h-4 w-4 text-primary" />
        <h3 className="font-display text-sm font-semibold text-foreground">Collection Insights</h3>
      </div>

      {/* Headline value */}
      <div className="rounded-xl bg-background/60 border border-border p-3 mb-3">
        <div className="flex items-baseline gap-2">
          <Coins className="h-4 w-4 text-primary" />
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-body">Estimated value</p>
        </div>
        <p className="font-display text-2xl font-bold text-primary mt-1">{formatPrice(insights.totalValue)}</p>
        <p className="text-[10px] text-muted-foreground font-body mt-0.5">
          {insights.totalCards} cards · {insights.uniqueCards} unique
          {insights.enchantedValue > 0 && ` · ${formatPrice(insights.enchantedValue)} in enchanted`}
        </p>
      </div>

      {/* Inkable split */}
      <div className="rounded-xl bg-background/60 border border-border p-3 mb-3">
        <div className="flex items-center justify-between mb-1.5">
          <div className="flex items-center gap-1.5">
            <Droplet className="h-3.5 w-3.5 text-primary" />
            <span className="text-[11px] font-medium text-foreground font-body">Inkable / Non-inkable</span>
          </div>
          <span className="text-[10px] text-muted-foreground font-body">{insights.inkable} / {insights.nonInkable}</span>
        </div>
        <div className="h-2 rounded-full bg-secondary overflow-hidden flex">
          <div className="h-full bg-primary transition-all" style={{ width: `${inkablePct}%` }} />
          <div className="h-full bg-muted-foreground/40 transition-all" style={{ width: `${100 - inkablePct}%` }} />
        </div>
      </div>

      {/* Top valued cards */}
      {insights.topValued.length > 0 && (
        <div className="rounded-xl bg-background/60 border border-border p-3 mb-3">
          <div className="flex items-center gap-1.5 mb-2">
            <Layers className="h-3.5 w-3.5 text-primary" />
            <span className="text-[11px] font-medium text-foreground font-body">Top-valued cards</span>
          </div>
          <ul className="space-y-1.5">
            {insights.topValued.map((c, i) => (
              <li key={i} className="flex items-center justify-between text-[11px] font-body">
                <span className="text-foreground truncate flex-1">{c.name}</span>
                <span className="text-primary font-medium ml-2">{formatPrice(c.value)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Weekly gainers/losers */}
      <PriceMoversCard />
    </div>
  );
}
